import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, CheckCircle2, Loader2 } from "lucide-react";
import { EASE } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full rounded-xl border border-line bg-ink/60 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-accent/60 focus:ring-1 focus:ring-accent/40";

export default function CatalogueModal({ open, onClose }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const [status, setStatus] = useState("idle");
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) return;
        try {
            setStatus("sending");
            await axios.post(`${API}/catalogue-requests`, form);
            setStatus("done");
        } catch (err) {
            console.error(err);
            setStatus("error");
        }
    };

    const close = () => {
        onClose();
        setTimeout(() => setStatus("idle"), 300);
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/80 p-4 backdrop-blur-md"
                    onClick={close}
                    data-testid="catalogue-modal"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 32, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.97 }}
                        transition={{ duration: 0.4, ease: EASE }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-md rounded-2xl border border-line bg-panel p-7 sm:p-8"
                    >
                        <button
                            data-testid="catalogue-close-button"
                            onClick={close}
                            aria-label="Close"
                            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-lg border border-line text-slate-400 transition-colors hover:border-accent/50 hover:text-white"
                        >
                            <X className="h-4 w-4" />
                        </button>

                        {status === "done" ? (
                            <div className="flex flex-col items-center py-6 text-center">
                                <CheckCircle2 className="h-11 w-11 text-mint" />
                                <h3 className="mt-5 font-display text-xl font-bold text-white">
                                    Catalogue requested.
                                </h3>
                                <p className="mt-3 max-w-xs text-sm leading-relaxed text-slate-400">
                                    We&apos;ve emailed you a confirmation — our
                                    team will share the full catalogue at{" "}
                                    <span className="text-white">
                                        {form.email}
                                    </span>
                                    .
                                </p>
                                <a
                                    data-testid="catalogue-download-link"
                                    href="/catalogue.pdf"
                                    download="Science-Surgical-Catalogue.pdf"
                                    className="mt-7 inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-ink transition-all duration-300 hover:scale-105"
                                >
                                    <Download className="h-4 w-4" />
                                    Download now
                                </a>
                            </div>
                        ) : (
                            <>
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">
                                    Product Catalogue
                                </p>
                                <h3 className="mt-3 font-display text-2xl font-bold text-white">
                                    Get it in your inbox.
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                                    Share your details and we&apos;ll email you
                                    our full product catalogue.
                                </p>

                                <form
                                    onSubmit={submit}
                                    className="mt-6 space-y-4"
                                    data-testid="catalogue-form"
                                >
                                    <input
                                        data-testid="catalogue-input-name"
                                        className={inputCls}
                                        placeholder="Full name *"
                                        value={form.name}
                                        onChange={set("name")}
                                        required
                                    />
                                    <input
                                        data-testid="catalogue-input-email"
                                        type="email"
                                        className={inputCls}
                                        placeholder="Email address *"
                                        value={form.email}
                                        onChange={set("email")}
                                        required
                                    />
                                    <input
                                        data-testid="catalogue-input-phone"
                                        className={inputCls}
                                        placeholder="Phone (optional)"
                                        value={form.phone}
                                        onChange={set("phone")}
                                    />
                                    {status === "error" && (
                                        <p
                                            data-testid="catalogue-form-error"
                                            className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-xs text-red-300"
                                        >
                                            Something went wrong — please try
                                            again or email us directly.
                                        </p>
                                    )}
                                    <button
                                        data-testid="catalogue-submit-button"
                                        type="submit"
                                        disabled={status === "sending"}
                                        className="flex w-full items-center justify-center gap-2 rounded-full bg-accent py-3.5 font-display text-sm font-bold uppercase tracking-wider text-ink transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,240,255,0.3)] disabled:opacity-60"
                                    >
                                        {status === "sending" ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Sending…
                                            </>
                                        ) : (
                                            "Email Me the Catalogue"
                                        )}
                                    </button>
                                </form>
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
